import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ImageResult } from '../types';
import { ImageSearchModal } from './ImageSearchModal';
import { useImageSearch } from '../hooks/useImageSearch';

interface BlockInserterProps {
  isOpen: boolean;
  onClose: () => void;
  onAddBlock: (blockType: string, attributes?: Record<string, any>, index?: number) => void;
  onAddImage?: (image: ImageResult) => void;
  position?: { index: number; x: number; y: number } | null;
}

export const BlockInserter: React.FC<BlockInserterProps> = ({ 
  isOpen, 
  onClose, 
  onAddBlock,
  onAddImage,
  position
}) => {
  const popupRef = useRef<HTMLDivElement>(null);
  
  // Use the image search hook
  const {
    showImageSearch,
    searchImages,
    searchLoading,
    selectedSources,
    hasMoreImages,
    handleImageSearch,
    handleSourceToggle,
    openImageSearch,
    closeImageSearch
  } = useImageSearch();

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleAddBlock = useCallback((blockType: string, attributes: Record<string, any> = {}) => {
    onAddBlock(blockType, attributes, position?.index);
    onClose();
  }, [onAddBlock, position?.index, onClose]);

  const handleImageSelect = (image: ImageResult) => {
    // Track Unsplash download if applicable
    if (image.source === 'unsplash' && image.downloadLocation) {
      console.log('📸 [UNSPLASH TRACKING] Triggering download tracking for:', image.downloadLocation);
      fetch('/api/unsplash-download', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          downloadLocation: image.downloadLocation
        })
      }).catch(error => {
        console.error('❌ [UNSPLASH TRACKING] Failed to track download:', error);
      });
    }
    
    if (onAddImage) {
      onAddImage(image);
    } else {
      // Fallback to adding as image block
      handleAddBlock('core/image', { 
        url: image.url, 
        alt: image.alt || image.caption, 
        caption: image.attribution || image.caption 
      });
    }
    onClose();
  };

  const handleLoadMoreImages = () => {
    handleImageSearch('', true); // Load more with empty query (uses lastSearchQuery)
  };

  if (!isOpen) return null;

  const blockTypes = [
    {
      name: 'core/paragraph',
      title: 'Paragraph',
      description: 'Start with the building block of all narrative.',
      icon: (
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" width="24" height="24" aria-hidden="true" focusable="false">
          <path d="M12 4H6v16h2v-6h4v6h2V4h-2z"></path>
        </svg>
      )
    },
    {
      name: 'core/heading',
      title: 'Heading',
      description: 'Introduce new sections and organize content to help visitors (and search engines) understand the structure of your content.',
      icon: (
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" width="24" height="24" aria-hidden="true" focusable="false">
          <path d="M5 4v3h5.5v12h3V7H19V4H5z"></path>
        </svg>
      )
    },
    {
      name: 'core/image',
      title: 'Image',
      description: 'Insert an image to make a visual statement.',
      icon: (
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" width="24" height="24" aria-hidden="true" focusable="false">
          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM5 4.5h14c.3 0 .5.2.5.5v8.4l-3-2.9c-.3-.3-.8-.3-1 0L11.9 14 9 12c-.3-.2-.6-.2-.8 0l-3.6 2.6V5c-.1-.3.1-.5.4-.5zm14 15H5c-.3 0-.5-.2-.5-.5v-2.4l4.1-3 3 1.9c.3.2.7.2.9-.1L16 12l3.5 3.4V19c0 .3-.2.5-.5.5z"></path>
        </svg>
      )
    },
    {
      name: 'core/list',
      title: 'List',
      description: 'Create a bulleted or numbered list.',
      icon: (
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" width="24" height="24" aria-hidden="true" focusable="false">
          <path d="M4 4v1.5h16V4H4zm8 8.5h8V11h-8v1.5zM4 20h16v-1.5H4V20zm4-8c0-1.1-.9-2-2-2s-2 .9-2 2 .9 2 2 2 2-.9 2-2z"></path>
        </svg>
      )
    },
    {
      name: 'core/quote',
      title: 'Quote',
      description: 'Give quoted text visual emphasis.',
      icon: (
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" width="24" height="24" aria-hidden="true" focusable="false">
          <path d="M13 6v6h5.2v4c0 .8-.2 1.4-.5 1.7-.6.6-1.6.6-2.5.5h-.3v1.5h.5c1 0 2.3-.1 3.3-1 .6-.6 1-1.6 1-2.8V6H13zm-9 6h5.2v4c0 .8-.2 1.4-.5 1.7-.6.6-1.6.6-2.5.5h-.3v1.5h.5c1 0 2.3-.1 3.3-1 .6-.6 1-1.6 1-2.8V6H4v6z"></path>
        </svg>
      )
    }
  ];

  const popupStyle: React.CSSProperties = {
    position: 'fixed',
    top: position ? `${position.y}px` : '50%',
    left: position ? `${position.x}px` : '50%',
    transform: position ? 'translate(-50%, -100%)' : 'translate(-50%, -50%)',
    opacity: 1,
    zIndex: 1000000,
    margin: 0,
    width: '550px',
    maxWidth: '90vw',
    boxShadow: '0 3px 30px rgba(25, 30, 35, 0.2)',
    borderRadius: '8px',
    border: '1px solid #ddd',
    backgroundColor: 'white',
    padding: '16px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif'
  };

  return (
    <div
      ref={popupRef}
      className="components-popover components-dropdown__content block-editor-inserter__popover is-quick is-positioned"
      style={popupStyle}
    >
      <div className="block-editor-inserter__quick-inserter">
        <div className="block-editor-inserter__block-list">
          <div className="block-editor-block-types-list" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '8px',
            padding: '16px'
          }}>
            {blockTypes.map((block) => (
              <button
                key={block.name}
                type="button"
                role="option"
                tabIndex={0}
                className="components-button block-editor-block-types-list__item"
                onClick={() => {
                  if (block.name === 'core/image') {
                    // Open image search modal for image blocks
                    openImageSearch('new-image');
                  } else {
                    const attributes: Record<string, any> = { content: '' };
                    if (block.name === 'core/heading') {
                      attributes.level = 2; // Default to H2
                    }
                    handleAddBlock(block.name, attributes);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '16px 12px',
                  border: 'none',
                  background: 'transparent',
                  textAlign: 'center',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  borderRadius: '4px',
                  transition: 'all 0.15s ease',
                  fontSize: '13px',
                  lineHeight: '1.4',
                  color: '#1e1e1e',
                  minHeight: '100px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f5f5f5';
                  e.currentTarget.style.color = '#007cba';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#1e1e1e';
                }}
              >
                <span className="block-editor-block-types-list__item-icon" style={{
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {block.icon}
                </span>
                <div style={{
                  fontWeight: '600',
                  fontSize: '13px',
                  color: 'inherit',
                  marginBottom: '4px'
                }}>
                  {block.title}
                </div>
                <div style={{
                  fontSize: '11px', 
                  color: '#666',
                  lineHeight: '1.3',
                  textAlign: 'center',
                  maxWidth: '100%',
                  wordWrap: 'break-word'
                }}>
                  {block.description}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* Image Search Modal */}
      <ImageSearchModal
        isOpen={showImageSearch}
        selectedSources={selectedSources}
        onSourceToggle={handleSourceToggle}
        onSearch={handleImageSearch}
        onSelect={handleImageSelect}
        onClose={closeImageSearch}
        images={searchImages}
        loading={searchLoading}
        hasMore={hasMoreImages}
        loadMore={handleLoadMoreImages}
      />
    </div>
  );
};